/**
 * VIVA HUB Email Signature app — branded signature builder with live preview
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Copy } from 'lucide-react'
import HubLayout from '../components/hub/HubLayout'
import { useSchedulerAuth } from '../contexts/SchedulerAuthContext'
import { useToast } from '../contexts/ToastContext'
import {
  getSignaturePreviewAssets,
  VIVA_SIGNATURE_ADDRESS,
  VIVA_SIGNATURE_DEFAULTS,
} from '../lib/email-signature/constants'
import { buildSignatureHtml, emptySignatureFields } from '../lib/email-signature/build-signature-html'
import { copySignatureHtml } from '../lib/email-signature/copy-signature-html'
import { formatPhoneUsa } from '../lib/email-signature/format-phone-usa'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

export default function HubEmailSignature() {
  const { user } = useSchedulerAuth()
  const { success: toastSuccess, error: toastError } = useToast()
  const [fields, setFields] = useState(() => emptySignatureFields())
  const [copying, setCopying] = useState(false)

  const sessionEmail = user?.email?.trim() || ''

  useEffect(() => {
    if (!user) return
    setFields((prev) => ({
      ...prev,
      name: user.name?.trim() || prev.name,
      phone: user.phone?.trim() ? formatPhoneUsa(user.phone) : prev.phone,
    }))
  }, [user?.id, user?.name, user?.phone])

  const previewAssets = useMemo(
    () =>
      getSignaturePreviewAssets(
        typeof window !== 'undefined' ? window.location.origin : 'https://vivapsychotherapy.com'
      ),
    []
  )

  const lockedFields = useMemo(
    () => ({
      ...fields,
      email: sessionEmail,
      websiteUrl: VIVA_SIGNATURE_DEFAULTS.websiteUrl,
      address: VIVA_SIGNATURE_ADDRESS,
    }),
    [fields, sessionEmail]
  )

  const signatureHtml = useMemo(
    () =>
      buildSignatureHtml({
        fields: lockedFields,
        assetBase: previewAssets.assetBase,
        logoUrl: previewAssets.logoUrl,
      }),
    [lockedFields, previewAssets]
  )

  const updateTextField = useCallback((key, value) => {
    setFields((prev) => ({
      ...prev,
      [key]: key === 'phone' ? formatPhoneUsa(value) : value,
    }))
  }, [])

  const handleCopyHtml = useCallback(async () => {
    setCopying(true)
    try {
      await copySignatureHtml(signatureHtml)
      toastSuccess('Signature copied. Paste it in Gmail Settings → General → Signature.')
    } catch {
      toastError('Could not copy automatically. Select the preview and copy manually.')
    } finally {
      setCopying(false)
    }
  }, [signatureHtml, toastSuccess, toastError])

  const handlePreviewKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      void handleCopyHtml()
    }
  }

  return (
    <HubLayout appName="Email Signature">
      <p className="mb-6 text-muted-foreground">
        Starts from Lidiana’s VIVA signature. Edit your name, titles, and phone, then copy the preview into Gmail
        (Google Workspace).
      </p>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Your details</CardTitle>
            <CardDescription>
              Email comes from your HUB profile. Website and office address are company details and cannot be
              changed here.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sig-name">Full name</Label>
              <Input
                id="sig-name"
                type="text"
                value={fields.name}
                onChange={(e) => updateTextField('name', e.target.value)}
                autoComplete="name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sig-role">Titles</Label>
              <Textarea
                id="sig-role"
                rows={4}
                value={fields.role}
                onChange={(e) => updateTextField('role', e.target.value)}
                className="resize-y"
              />
              <p className="text-xs text-muted-foreground">One title per line.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sig-phone">Phone</Label>
              <Input
                id="sig-phone"
                type="tel"
                value={fields.phone}
                onChange={(e) => updateTextField('phone', e.target.value)}
                autoComplete="tel"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sig-email">Email</Label>
              <Input
                id="sig-email"
                type="email"
                value={sessionEmail}
                readOnly
                aria-readonly="true"
                className="cursor-not-allowed bg-muted"
                autoComplete="email"
              />
              <p className="text-xs text-muted-foreground">
                From your HUB profile. Change it in Profile if needed.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sig-website">Website</Label>
              <Input
                id="sig-website"
                type="text"
                value={VIVA_SIGNATURE_DEFAULTS.websiteUrl}
                readOnly
                aria-readonly="true"
                className="cursor-not-allowed bg-muted"
              />
              <p className="text-xs text-muted-foreground">Company website — not editable.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sig-address">Address</Label>
              <Input
                id="sig-address"
                type="text"
                value={VIVA_SIGNATURE_ADDRESS}
                readOnly
                aria-readonly="true"
                className="cursor-not-allowed bg-muted"
              />
              <p className="text-xs text-muted-foreground">Office address — not editable.</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>Click the signature to copy it for Gmail.</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                role="button"
                tabIndex={0}
                aria-label="Click to copy signature"
                aria-busy={copying}
                onClick={() => void handleCopyHtml()}
                onKeyDown={handlePreviewKeyDown}
                className={cn(
                  'group relative cursor-pointer overflow-x-auto rounded-xl border border-border bg-background p-6 shadow-inner transition-all',
                  'hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'sm:p-8',
                  copying && 'pointer-events-none opacity-70'
                )}
              >
                <div className="pointer-events-none" dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-primary/10 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
                  <span className="flex items-center gap-2 rounded-full bg-card/95 px-3 py-1.5 text-xs font-semibold text-foreground shadow-md ring-1 ring-border">
                    <Copy className="h-3.5 w-3.5" />
                    Click to copy
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Add this signature in Gmail</CardTitle>
              <CardDescription>
                VIVA email is Google Workspace. Follow these steps in Gmail on a computer (not the mobile app).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4">
                {[
                  {
                    title: 'Copy the signature',
                    body: 'Click the preview above. That copies the formatted signature.',
                  },
                  {
                    title: 'Open Gmail signature settings',
                    body: (
                      <>
                        In Gmail, click the gear → <strong>See all settings</strong> → stay on the{' '}
                        <strong>General</strong> tab → scroll to <strong>Signature</strong>. Or open{' '}
                        <a
                          href="https://mail.google.com/mail/#settings/general"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary underline underline-offset-2 hover:text-primary/90"
                        >
                          Gmail settings
                        </a>{' '}
                        directly.
                      </>
                    ),
                  },
                  {
                    title: 'Paste and save',
                    body: (
                      <>
                        Click <strong>+ Create new</strong>, name it (for example “VIVA”), paste with Cmd+V (Mac) or
                        Ctrl+V (Windows), then scroll down and click <strong>Save Changes</strong>.
                      </>
                    ),
                  },
                  {
                    title: 'Use it on new emails and replies',
                    body: (
                      <>
                        Still in Signature settings, set <strong>FOR NEW EMAILS USE</strong> and{' '}
                        <strong>ON REPLY/FORWARD USE</strong> to your new signature, then Save Changes again.
                      </>
                    ),
                  },
                ].map((step, index) => (
                  <li key={step.title} className="flex gap-3">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">{step.title}</h3>
                      <p className="mt-0.5 text-sm text-muted-foreground">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </HubLayout>
  )
}
